/**
 * PlayerProjectilePool — pooled projectile system for player-fired projectiles.
 *
 * Manages the logical state of player projectiles: position, velocity, range,
 * piercing, splits, on-hit effects, and AoE impacts. Pure logic — no Phaser
 * dependencies. The integration phase wires this to Phaser's physics and
 * rendering layer.
 *
 * Projectile types: bolt (single-target line), cone (area arc), orb (AoE on
 * impact), line (ground effect). Used by Fire (Vas Flam, Conduit), Lightning
 * (Bolt, Arc), Poison (Corrode, Acid Rain), Earth (Rupture), and Astral
 * (Slingshot, Meteo).
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProjectileType = 'bolt' | 'cone' | 'orb' | 'line';

export interface PlayerProjectileConfig {
  readonly type: ProjectileType;
  readonly damage: number;
  /** Pixels per second. */
  readonly speed: number;
  /** Maximum travel distance in tiles. */
  readonly rangeTiles: number;
  readonly sourceSkillId: string;

  // Shape
  readonly widthPx?: number;
  readonly heightPx?: number;
  /** Cone spread angle in degrees (for cone type). */
  readonly coneDegrees?: number;

  // Behavior
  /** Passes through enemies instead of stopping on first hit. */
  readonly piercing?: boolean;
  /** Maximum number of enemies hit (for piercing projectiles). */
  readonly maxTargets?: number;
  /** Fork into N sub-projectiles on impact or at range. */
  readonly splitCount?: number;
  /** Damage multiplier for split sub-projectiles. */
  readonly splitDamageMultiplier?: number;

  // On-hit effects
  readonly statusToApply?: string;
  readonly statusDurationMs?: number;
  readonly statusIntensity?: number;
  readonly knockbackTiles?: number;
  readonly staggerMs?: number;

  // AoE on impact (for orb type)
  readonly impactRadiusTiles?: number;
  readonly impactDamage?: number;

  // Trail
  readonly leavesTrail?: boolean;
  readonly trailDurationMs?: number;
  readonly trailDamageMultiplier?: number;
}

/** Runtime state of an active projectile. */
export interface ActiveProjectile {
  readonly id: number;
  readonly config: PlayerProjectileConfig;
  x: number;
  y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly startX: number;
  readonly startY: number;
  distanceTraveled: number;
  hitCount: number;
  active: boolean;
}

/** Emitted when a projectile expires or impacts. */
export interface ProjectileEvent {
  readonly projectileId: number;
  readonly type: 'expired' | 'maxRange' | 'hit' | 'split';
  readonly config: PlayerProjectileConfig;
  readonly x: number;
  readonly y: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import { TILE_SIZE } from '../utils/Constants.js';

const DEFAULT_POOL_SIZE = 32;

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class PlayerProjectilePool {
  private readonly projectiles: ActiveProjectile[] = [];
  private readonly events: ProjectileEvent[] = [];
  private nextId = 1;
  private readonly poolSize: number;

  constructor(poolSize = DEFAULT_POOL_SIZE) {
    this.poolSize = poolSize;
  }

  /**
   * Fire a projectile from a position in a direction.
   * Returns the projectile ID, or -1 if the pool is exhausted.
   */
  fire(
    config: PlayerProjectileConfig,
    fromX: number,
    fromY: number,
    dirX: number,
    dirY: number,
  ): number {
    // Normalize direction
    const mag = Math.sqrt(dirX * dirX + dirY * dirY);
    if (mag === 0) return -1;
    const nx = dirX / mag;
    const ny = dirY / mag;

    // Check pool capacity
    const activeCount = this.projectiles.filter(p => p.active).length;
    if (activeCount >= this.poolSize) return -1;

    const id = this.nextId++;
    const projectile: ActiveProjectile = {
      id,
      config,
      x: fromX,
      y: fromY,
      dirX: nx,
      dirY: ny,
      startX: fromX,
      startY: fromY,
      distanceTraveled: 0,
      hitCount: 0,
      active: true,
    };

    this.projectiles.push(projectile);
    return id;
  }

  /**
   * Per-frame update: move projectiles, check range, expire.
   * Returns events generated this frame (hits, expirations, splits).
   */
  update(delta: number): ProjectileEvent[] {
    this.events.length = 0;

    for (const proj of this.projectiles) {
      if (!proj.active) continue;

      // Move
      const moveDistance = proj.config.speed * (delta / 1000);
      proj.x += proj.dirX * moveDistance;
      proj.y += proj.dirY * moveDistance;
      proj.distanceTraveled += moveDistance;

      // Check max range
      const maxRangePx = proj.config.rangeTiles * TILE_SIZE;
      if (proj.distanceTraveled >= maxRangePx) {
        proj.active = false;

        // Handle splits at max range
        if (proj.config.splitCount && proj.config.splitCount > 0) {
          this.events.push({
            projectileId: proj.id,
            type: 'split',
            config: proj.config,
            x: proj.x,
            y: proj.y,
          });
        } else {
          this.events.push({
            projectileId: proj.id,
            type: 'maxRange',
            config: proj.config,
            x: proj.x,
            y: proj.y,
          });
        }
      }
    }

    return [...this.events];
  }

  /**
   * Register a hit on a projectile. Called by the integration layer
   * when a collision is detected.
   *
   * Returns the hit info for applying damage/effects, or null if
   * the projectile is inactive or has reached its max targets.
   */
  registerHit(projectileId: number): {
    damage: number;
    config: PlayerProjectileConfig;
    shouldDeactivate: boolean;
  } | null {
    const proj = this.projectiles.find(p => p.id === projectileId && p.active);
    if (!proj) return null;

    proj.hitCount++;
    const maxTargets = proj.config.maxTargets ?? 1;
    const isPiercing = proj.config.piercing ?? false;
    const shouldDeactivate = !isPiercing || proj.hitCount >= maxTargets;

    if (shouldDeactivate) {
      proj.active = false;
    }

    return {
      damage: proj.config.damage,
      config: proj.config,
      shouldDeactivate,
    };
  }

  /** Get a projectile by ID. Returns undefined if not found. */
  get(projectileId: number): ActiveProjectile | undefined {
    return this.projectiles.find(p => p.id === projectileId);
  }

  /** Get all currently active projectiles. */
  getActive(): readonly ActiveProjectile[] {
    return this.projectiles.filter(p => p.active);
  }

  /** Get the count of active projectiles. */
  get activeCount(): number {
    return this.projectiles.filter(p => p.active).length;
  }

  /** Deactivate a projectile by ID. */
  deactivate(projectileId: number): void {
    const proj = this.projectiles.find(p => p.id === projectileId);
    if (proj) proj.active = false;
  }

  /** Clean up inactive projectiles from the internal array to prevent unbounded growth. */
  cleanup(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      if (!this.projectiles[i].active) {
        this.projectiles.splice(i, 1);
      }
    }
  }

  /** Clear all projectiles and events. Called on scene shutdown to prevent leaks across run restarts. */
  destroy(): void {
    this.projectiles.length = 0;
    this.events.length = 0;
    this.nextId = 1;
  }
}
